// Shared PhotoSwipe lightbox factory.
//
// Single source of truth for the lightbox behavior used across the app:
// Stimulus (photoswipe_controller.js, email_body_images_controller.js) and
// Preact (MessageList.jsx / chat). Keeps the data-attribute contract
// (data-photoswipe / data-type / data-caption / data-download-url /
// data-pswp-src / data-pswp-srcset) and custom UI (download button,
// caption) identical everywhere PhotoSwipe is used.
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import PhotoSwipe from 'photoswipe'

const NON_IMAGE_TYPES = ['iframe', 'html5video', 'html5audio']

/**
 * createPhotoSwipeLightbox — builds and initializes a fully configured
 * PhotoSwipeLightbox instance for the given gallery root.
 *
 * @param {Element|string} gallery - root element (or selector) containing the
 *   `a[data-photoswipe]` gallery items.
 * @param {object} [options] - extra/overriding PhotoSwipeLightbox options.
 * @returns {PhotoSwipeLightbox} initialized lightbox instance — call
 *   `.destroy()` when the gallery unmounts.
 */
export function createPhotoSwipeLightbox(gallery, options = {}) {
  const lightbox = new PhotoSwipeLightbox({
    children: 'a[data-photoswipe]',
    pswpModule: PhotoSwipe,
    preloadFirstSlide: true,
    // Only preload the current + adjacent slide (PhotoSwipe default) —
    // avoids downloading every slide's image on gallery open, which is
    // especially costly on mobile connections for large galleries.
    preload: [1, 1],
    // Always preserve aspect ratio on all devices.
    // 'fit' preserves aspect ratio, 'fill' would stretch/crop images.
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 'fit',
    maxZoomLevel: 4, // Allow zooming in up to 4x
    ...options,
    gallery,
  })

  // Resolve image dimensions lazily from the already-rendered thumbnail so
  // we don't need to preload images before the lightbox is ready.
  lightbox.addFilter('itemData', (itemData, _index) => {
    const el = itemData.element
    if (!el || el.dataset.type !== 'image') return itemData
    if (itemData.width && itemData.height) return itemData

    // Try to read dimensions from the visible <img> inside the link's parent
    const img = el.closest('.chat-attachment-img-wrap')?.querySelector('img')
    if (img && img.naturalWidth > 0) {
      itemData.width = img.naturalWidth
      itemData.height = img.naturalHeight
    } else {
      // Fallback: will be corrected once the full image loads
      itemData.width = 1600
      itemData.height = 1200
    }
    return itemData
  })

  // Disable loading indicator for iframe/video/audio content
  lightbox.addFilter('isContentLoading', (isContentLoading, content) => {
    const type = content.data.element?.dataset?.type
    if (NON_IMAGE_TYPES.includes(type)) {
      return false // Never show loading indicator for custom content
    }
    return isContentLoading
  })

  // Register custom UI elements (download button, caption, etc.)
  lightbox.on('uiRegister', () => {
    // Download button
    lightbox.pswp.ui.registerElement({
      name: 'download-button',
      order: 8,
      isButton: true,
      tagName: 'a',
      ariaLabel: 'Download',
      html: {
        isCustomSVG: true,
        inner: '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
        outlineID: 'pswp__icn-download'
      },
      onInit: (el, pswp) => {
        el.setAttribute('download', '')
        el.setAttribute('target', '_blank')
        el.setAttribute('rel', 'noopener')

        const updateDownloadLink = () => {
          const currSlideElement = pswp.currSlide?.data?.element
          const downloadUrl = currSlideElement?.dataset?.downloadUrl || currSlideElement?.getAttribute('href') || pswp.currSlide?.data?.src

          if (downloadUrl) {
            el.setAttribute('href', downloadUrl)
          } else {
            el.removeAttribute('href')
          }
        }

        pswp.on('change', updateDownloadLink)
        pswp.on('openingAnimationEnd', updateDownloadLink)
      }
    })

    // Caption support
    lightbox.pswp.ui.registerElement({
      name: 'caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      html: '',
      onInit: (el) => {
        lightbox.pswp.on('change', () => {
          const currSlideElement = lightbox.pswp.currSlide.data.element
          let captionHTML = ''

          if (currSlideElement) {
            const caption = currSlideElement.dataset.caption || currSlideElement.getAttribute('title')
            if (caption) {
              captionHTML = caption
            }
          }

          el.innerHTML = captionHTML || ''
        })
      }
    })
  })

  // Handle non-image types (iframe, video, audio)
  lightbox.on('contentLoad', (e) => {
    const { content } = e
    const type = content.data.element?.dataset.type

    if (NON_IMAGE_TYPES.includes(type)) {
      // Prevent default image loading
      e.preventDefault()

      // Create container for iframe/video/audio
      content.element = document.createElement('div')
      content.element.className = type === 'iframe' ? 'pswp__iframe-container' :
                                 type === 'html5video' ? 'pswp__video-container' :
                                 'pswp__audio-container'

      // Create the actual content element (iframe, video, or audio)
      if (type === 'iframe') {
        const iframe = document.createElement('iframe')
        iframe.src = content.data.src
        iframe.setAttribute('frameborder', '0')
        iframe.setAttribute('allowfullscreen', '')
        content.element.appendChild(iframe)
      } else if (type === 'html5video') {
        const video = document.createElement('video')
        video.controls = true
        const source = document.createElement('source')
        source.src = content.data.src
        video.appendChild(source)
        content.element.appendChild(video)
      } else if (type === 'html5audio') {
        const audio = document.createElement('audio')
        audio.controls = true
        const source = document.createElement('source')
        source.src = content.data.src
        audio.appendChild(source)
        content.element.appendChild(audio)
      }
    }
  })

  lightbox.init()
  return lightbox
}
